import { useCurrentCloudProvider } from '@labrav/common'
import { PlanType } from '@labrav/react-components'
import React from 'react'
import { useStyles } from './PrivateOfferCreation.styles'
import { setSingleFieldErrorOnPrivateOfferCreation } from '../../../modules/privateOffer/privateOfferCreation/action'
import type {
  KeyValue,
  OffersWithErrors,
} from '../../../modules/privateOffer/privateOfferCreation/types'
import type { Product } from '../../../modules/productsListing/types'
import { MetaDataComponent } from './MetaDataComponent'
import { Dimension, EulaType } from '../../../modules/privateOffer/types'
import { planTypesForCommonFields } from '../PricingComponent/utils'
import { AzurePricingSection } from '../AzurePricingSection'
import { CustomerTerm } from '../CustomerTerm'
import { Details } from '../Details'
import { PricingComponent } from '../PricingComponent'
import { PrivateOfferDimensions } from '../PrivateOfferDimensions'
import { Renewal, RenewalMicrosoft } from '../Renewal'
import { useDispatch } from '../../../store'
import { Eula } from './Eula'
import { getEulaOption, getEulaSectionConfig } from './utils'

export type PrivateOfferCreationProps = {
  product: Product
  selectedEulaFiles: File[]
  setEulaFile: (files: File[]) => void
  disableUpload: boolean
  handleChange: (data: KeyValue) => void
  deleteRow: (index: number, key: string) => () => void
  updateValuesInArray: (
    index: number,
    key: string,
    type?: string
  ) => (data: KeyValue) => void
  addRow: (key: string, type?: string) => void
  privateOffer: OffersWithErrors
  updateStandardDimensions: (index: number, dimension: Dimension) => void
}
export const PrivateOfferCreation: React.FC<PrivateOfferCreationProps> = ({
  selectedEulaFiles,
  setEulaFile,
  handleChange,
  disableUpload,
  deleteRow,
  updateValuesInArray,
  addRow,
  privateOffer,
  product,
  updateStandardDimensions,
}) => {
  const { classes } = useStyles()
  const dispatch = useDispatch()
  const isProductPlanTypeProfessionalService =
    product?.productType === PlanType.PROFESSIONAL_SERVICES

  const currentCloudProvider = useCurrentCloudProvider()

  const {
    eulaSubHeading,
    eulaTooltipLabel,
    eulaSectionHeading,
    shouldShowEulaOptionSelectionRadioButtons,
    eulaProps,
  } = getEulaSectionConfig(
    currentCloudProvider,
    isProductPlanTypeProfessionalService
  )

  const updateEulaFileErr = async (eulaErr: string) => {
    await dispatch(
      setSingleFieldErrorOnPrivateOfferCreation(
        'eulaFile',
        product.productId,
        eulaErr
      )
    )
  }
  return (
    <div className={classes.root}>
      <Details
        privateOfferWithError={privateOffer}
        addOfferRecipient={addRow}
        deleteOfferRecipient={deleteRow}
        updateRecipientsData={updateValuesInArray}
        handleDetailsChange={handleChange}
        planType={product?.productType || PlanType.SAAS_CONTRACT}
      />
      {currentCloudProvider === 'AWS' ? (
        <Renewal
          renewal={privateOffer?.renewal || false}
          channel={privateOffer?.renewalChannel || null}
          handleDetailsChange={handleChange}
          offerType={'offer'}
        />
      ) : currentCloudProvider === 'AZURE' ? (
        <RenewalMicrosoft
          existingContractRenewal={
            privateOffer?.existingContractRenewal || false
          }
          handleDetailsChange={handleChange}
        />
      ) : (
        <></>
      )}
      {currentCloudProvider === 'AZURE' ? (
        <>
          <CustomerTerm
            privateOfferWithError={privateOffer}
            handleDetailsChange={handleChange}
          />
          <AzurePricingSection
            privateOfferWithError={privateOffer}
            handleDetailsChange={handleChange}
            addRow={addRow}
            deleteRow={deleteRow}
            product={product}
          />
        </>
      ) : (
        <></>
      )}
      {currentCloudProvider === 'AWS' ? (
        <>
          {product?.productType &&
          planTypesForCommonFields.includes(product?.productType) ? (
            <PricingComponent
              heading={'Pricing'}
              subHeading={'Additional pricing configuration'}
              updateInstallments={updateValuesInArray}
              deleteInstallmentsRow={deleteRow}
              handlePricingCheckbox={handleChange}
              flexiblePaymentTerms={privateOffer.flexiblePaymentTerms!}
              installments={privateOffer.installments || []}
              addInstallmentRows={addRow}
              planType={product?.productType || PlanType.SAAS_CONTRACT}
              errors={[
                ...((privateOffer.errors.installments as Array<
                  Record<string, unknown>
                >) ?? []),
              ]}
            />
          ) : (
            <></>
          )}
          <PrivateOfferDimensions
            dimensions={privateOffer.dimensions || []}
            flexiblePaymentTerms={privateOffer.flexiblePaymentTerms!}
            entitlementOption={privateOffer.entitlementOption}
            product={product}
            handleUpdateDimensionRow={updateValuesInArray}
            deleteDimensionsRow={deleteRow}
            addDimension={addRow}
            handleChange={handleChange}
            updateStandardDimensions={updateStandardDimensions}
            isResale={false}
            dimensionsError={[
              ...((privateOffer.errors.dimensions as Array<
                Record<string, unknown>
              >) ?? []),
            ]}
          />
        </>
      ) : (
        <></>
      )}

      <Eula
        selectedEulaFiles={selectedEulaFiles}
        setEulaFile={setEulaFile}
        disableUpload={disableUpload}
        handleChange={handleChange}
        eulaType={privateOffer.eulaType}
        fileUploadHeading={'Service agreement documents'}
        fileUploadSubHeading={
          'Upload your custom EULA(s) for this specific private offer'
        }
        heading={eulaSectionHeading}
        subHeading={eulaSubHeading}
        tooltipLabel={eulaTooltipLabel}
        deleteLink={deleteRow}
        fileLinks={privateOffer.eulaFile !== null ? privateOffer.eulaFile : []}
        eulaOptions={getEulaOption(
          product?.productType || PlanType.SAAS_CONTRACT
        )}
        showFileUploadForOptions={[
          EulaType.STANDARD,
          EulaType.PUBLIC,
          EulaType.CUSTOM,
        ]}
        shouldShowEulaOptionSelectionRadioButtons={
          shouldShowEulaOptionSelectionRadioButtons
        }
        {...eulaProps}
        updateErr={updateEulaFileErr}
        showRealName
        checkDuplicate
      />
      <MetaDataComponent
        metaData={privateOffer.metaData || []}
        updateMetaData={updateValuesInArray}
        deleteMetaDataRow={deleteRow}
        addMetaDataRows={addRow}
        errors={[
          ...((privateOffer.errors.metaData as Array<
            Record<string, unknown>
          >) ?? []),
        ]}
      />
    </div>
  )
}
